import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { VentanaCopiloto } from '@/components/copiloto/VentanaCopiloto';
import { BuscadorGlobal } from './BuscadorGlobal';
import { useSocket } from '@/hooks/useSocket';
import { useAuth } from '@/hooks/useAuth';
import { shouldAutoStartTour, startTour } from '@/lib/tour';
import { Link } from 'react-router-dom';
import { esAmbientePruebas } from '@/lib/simulador-chat';
import { GuiaProvider } from '@/components/guia/GuiaEnCurso';
import { FranjaVerComo } from './VerComo';

const AMBIENTE_PRUEBAS = esAmbientePruebas(import.meta.env.MODE);

/** Shell autenticado: sidebar + header + área de contenido con <Outlet>. */
export function Layout() {
  // Mantiene viva la conexión de tiempo real mientras hay sesión.
  useSocket();

  // Tour de bienvenida: se auto-lanza UNA vez por navegador al primer login de
  // un ADMIN (con un respiro para que el sidebar ya esté pintado). Re-lanzable
  // desde el menú de Ayuda del header.
  const { user } = useAuth();
  useEffect(() => {
    if (!shouldAutoStartTour(user?.role)) return;
    const t = setTimeout(startTour, 800);
    return () => clearTimeout(t);
  }, [user?.role]);

  return (
    // "Muéstrame": la guía vive sobre todo el panel para seguir a la persona de
    // pantalla en pantalla, y la ventanita del Asistente la puede arrancar.
    <GuiaProvider>
    {/* 21-oct, macOS 27: la ventana. Barra lateral opaca pegada al borde y contenido blanco. */}
    <div className="flex h-full w-full overflow-hidden bg-white dark:bg-slate-950">
      {/*
        A3 · El buscador global. Va en el Layout y no en cada pantalla por el
        mismo motivo que el copiloto: se busca desde donde uno esté. Arranca
        cerrado y se abre con Ctrl+K (o ⌘K); mientras está cerrado no pinta
        nada, así que no le quita lugar a ninguna pantalla.
      */}
      <BuscadorGlobal />
      <Sidebar />
      {/* 21-oct, macOS 27: sin marco flotante; el contenido va blanco de borde a borde. */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white dark:bg-slate-950">
        {/*
          AMBIENTE DE PRUEBA: una barra que no se puede confundir con producción,
          con la puerta al Simulador. Fuera de `vite --mode pruebas` no existe.
        */}
        {AMBIENTE_PRUEBAS && (
          <div
            role="status"
            className="flex items-center justify-between gap-3 bg-amber-400 px-4 py-1.5 text-sm font-medium text-amber-950"
          >
            <span>AMBIENTE DE PRUEBA · nada sale a WhatsApp · datos inventados</span>
            <Link to="/simulador" className="rounded bg-amber-950 px-2.5 py-0.5 text-xs font-semibold text-amber-50 hover:bg-amber-900">
              Abrir el simulador
            </Link>
          </div>
        )}
        <FranjaVerComo />
        <Header />
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
      {/*
        B5 · El asistente interno. Va en el Layout y no en cada pantalla: se
        pregunta desde donde uno esté, que es el punto. Flota sobre el
        contenido sin empujarlo, y arranca cerrado -- un botón chico en la
        esquina, no algo que haya que cerrar antes de trabajar.

        Apagado o con el directorio vacío, la ventanita se abre igual y explica
        qué falta. Un botón que no hace nada seria peor.
      */}
      <VentanaCopiloto />
    </div>
    </GuiaProvider>
  );
}
